import React, { useEffect, useState, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import { NSInputNumberStepper, NSLabel } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import './ProFormaExpenses.scss';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import OperatingExpensesTable from './OperatingExpensesTable/OperatingExpensesTable';
import CapitalExpendituresTable from './CapitalExpendituresTable/CapitalExpendituresTable';
import BaseTotalsTable from '../BaseTotalsTable/BaseTotalsTable';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';

export interface IProFormaExpensesProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaExpenses = ({ operationBeginIndex, baseCashFlowColumns }: IProFormaExpensesProps) => {
    const {
        expenses, operatingInfo, handleUpdateProFormaOperations, operationsCashFlow,
    } = useProFormaSocketContext();

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('expenses-table', ['income-table', 'permanent-debt-table']);

    const [yearTaxes, setYearTaxes] = useState<number[]>(expenses?.taxPercentagePerYear || []);

    useEffect(() => {
        if (expenses) {
            setYearTaxes(expenses.taxPercentagePerYear || []);
        }
    }, [expenses]);

    const handleUpdatePropertyTaxPercentage = (propTaxPercentage: number, index: number) => {
        if (!operatingInfo || !operatingInfo.id) return;

        const updatedTaxes = [...yearTaxes];
        updatedTaxes.splice(index, 1, propTaxPercentage);
        setYearTaxes(updatedTaxes);
        handleUpdateProFormaOperations(operatingInfo.id, { taxPercentagePerYear: updatedTaxes });
    };

    const renderPropYearRow = (propTaxPercentage: number, index: number) => (
        <div className="d-flex align-items-center mt-2" key={`prop-tax-year-row-${index}`}>
            <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark">
                Prop tax: year
                {' '}
                {index + 1}
                {' '}
                anticipated % of full
            </NSLabel>
            <NSInputNumberStepper
                name={`propTax_${index}`}
                value={propTaxPercentage}
                onChange={(value: number) => handleUpdatePropertyTaxPercentage(value, index)}
                min={0}
                max={100}
                step={1}
                percentage
                inputGroupClassName="ProFormaPermanentDebt__input-width"
            />
        </div>
    );

    const cashFlowValues = useMemo(() => {
        const values: Record<string, any[]> = {
            netOperatingIncome: [],
            cashFlowFromOperations: [],
        };
        operationsCashFlow?.forEach((cashFlow, index) => {
            values.netOperatingIncome.push(cashFlow?.netOperatingIncome || 0);
            values.cashFlowFromOperations.push(cashFlow?.cashFlowFromOperations || 0);
        });
        return values;
    }, [operationsCashFlow]);

    return (
        <div className="ProFormaExpenses p-3">
            <RenderIf isTrue={yearTaxes.length}>
                <div className="w-50 pb-3">
                    {yearTaxes.map((item, index) => renderPropYearRow(item, index))}
                </div>
                <hr className="my-3" />
            </RenderIf>

            <div
                className={classNames({
                    'mr-n3': operationBeginIndex,
                })}
            >
                <div
                    className="scrollable-container"
                    ref={createRefHandler(tableRef)}
                    onScroll={() => handleTableScroll('table')}
                >
                    <OperatingExpensesTable
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                    <BaseTotalsTable
                        tableName="Net operating income"
                        totalAmount={expenses?.netOperatingIncome || 0}
                        cashFlowValues={cashFlowValues.netOperatingIncome}
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                    <CapitalExpendituresTable
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                    <BaseTotalsTable
                        tableName="Cash flow from operations"
                        totalAmount={expenses?.totalCashFlowOperations || 0}
                        cashFlowValues={cashFlowValues.cashFlowFromOperations}
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProFormaExpenses;
